import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Fingerprint, RefreshCw, Loader2, ShieldCheck } from 'lucide-react';
import api from '../../services/api';

function timeAgo(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

const ROLE_COLOR = {
  resident: 'bg-blue-500',
  guard: 'bg-amber-500',
  management: 'bg-emerald-500',
  super_admin: 'bg-purple-500',
};

/**
 * SuperAdmin panel: cross-community passkey (Face ID / Fingerprint) audit.
 * Answers "who added a new device on July 15?" at a glance.
 */
export function SAPasskeyDevicesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/webauthn/admin/all-devices', {
        params: { limit: 100 },
      });
      setRows(data.devices || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = expanded ? rows : rows.slice(0, 5);
  // Adoption metric: distinct users with ≥1 passkey.
  const uniqueUsers = new Set(rows.map(r => r.user_id)).size;

  return (
    <Card className="shadow-lg border-0 mb-6" data-testid="sa-passkey-devices-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-gk-navy">
            <Fingerprint className="w-5 h-5 text-gk-teal" />
            Passkey Devices (Face ID / Fingerprint Audit)
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="sa-passkey-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
          <Badge className="bg-gk-teal text-white" data-testid="sa-passkey-total-devices">
            {rows.length} device{rows.length === 1 ? '' : 's'}
          </Badge>
          <Badge variant="outline" data-testid="sa-passkey-unique-users">
            {uniqueUsers} user{uniqueUsers === 1 ? '' : 's'} enrolled
          </Badge>
          <span className="text-slate-500 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" />
            Sorted newest-first
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-4 text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-400 italic py-4 text-center" data-testid="sa-passkey-empty">
            No passkeys enrolled yet. Once residents enable Face ID from their Profile page, entries will appear here.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                    <th className="text-left py-2 px-2">User</th>
                    <th className="text-left py-2 px-2">Device</th>
                    <th className="text-left py-2 px-2 hidden md:table-cell">Community</th>
                    <th className="text-left py-2 px-2">Enrolled</th>
                    <th className="text-left py-2 px-2 hidden sm:table-cell">Last used</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((d) => (
                    <tr
                      key={d.id}
                      data-testid={`sa-passkey-row-${d.id}`}
                      className="border-b last:border-0 hover:bg-slate-50"
                    >
                      <td className="py-2 px-2 align-top">
                        <div className="font-semibold text-slate-800 truncate max-w-[180px]">
                          {d.user_name || d.user_email || '—'}
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5">
                          <span className={`inline-block w-2 h-2 rounded-full ${ROLE_COLOR[d.user_role] || 'bg-slate-400'}`}></span>
                          {d.user_role || 'user'}
                          {d.villa_number && <span className="text-slate-400"> · Villa {d.villa_number}</span>}
                        </div>
                      </td>
                      <td className="py-2 px-2 align-top">
                        <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded" data-testid="sa-passkey-device-name">
                          {d.device_name}
                        </span>
                      </td>
                      <td className="py-2 px-2 align-top hidden md:table-cell text-xs text-slate-600 truncate max-w-[160px]">
                        {d.community_name || '—'}
                      </td>
                      <td className="py-2 px-2 align-top text-xs">
                        <div className="text-slate-700">{timeAgo(d.created_at)}</div>
                        <div className="text-[10px] text-slate-400">
                          {d.created_at ? new Date(d.created_at).toLocaleString('en-IN',
                            { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'short' }) : ''}
                        </div>
                      </td>
                      <td className="py-2 px-2 align-top hidden sm:table-cell text-xs text-slate-500">
                        {timeAgo(d.last_used_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length > 5 && (
              <div className="text-center mt-3">
                <Button
                  variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}
                  data-testid="sa-passkey-expand-toggle"
                  className="text-gk-teal hover:bg-gk-teal/10"
                >
                  {expanded ? 'Show fewer' : `Show all ${rows.length} devices`}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
